import { Diagnostic } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { MathNode, parse } from 'mathjs';

import {
  ALL_CONSTANTS,
  ALL_FUNCTIONS,
  DEFAULT_FORMULA_EDITOR_CONFIG,
  FormulaEditorConfig,
  getEffectiveConstants,
  getEffectiveFunctions,
  getEffectiveOperators,
  getInConditionalOnlyOperators,
  OPERATOR_TOKEN_ALIASES
} from './formula-editor-config';

import { FormulaValidationResult } from '../models/formula-result.model';
export { FormulaValidationResult } from '../models/formula-result.model';

/** A single operator occurrence: the typed token plus the mathjs function it maps to. */
interface FormulaOperatorUsage {
  /** Token as written in the formula (e.g. `and`, `!`) */
  op: string;
  /** mathjs function backing the operator (e.g. `and`, `factorial`) */
  fn: string;
}

/**
 * True when an operator occurrence matches one of the configured conditional-only operators.
 *
 * The token must be listed **and** resolve to the expected mathjs function, so a configured
 * `!` (logical NOT shorthand) never matches `5!` (`fn: 'factorial'`).
 */
function isInConditionalOnlyOperator({ op, fn }: FormulaOperatorUsage, inConditionalOnlyOperators: ReadonlySet<string>): boolean {
  if (!inConditionalOnlyOperators.has(op)) {
    return false;
  }
  return fn === op || OPERATOR_TOKEN_ALIASES[op] === fn;
}

/**
 * Information extracted from a mathjs AST in a single traversal.
 */
interface FormulaAstInfo {
  symbols: string[];
  operators: FormulaOperatorUsage[];
  functions: string[];
  /** True if the formula contains at least one ternary (`?:`) ConditionalNode */
  hasConditional: boolean;
  /** Conditional-only operators (per config) found **outside** a ConditionalNode context */
  outsideConditionalOperators: string[];
}

/**
 * Extracts all symbol names, operators, and function names from a mathjs
 * parsed expression tree in a single traversal.
 */
function extractAstInfo(node: MathNode, inConditionalOnlyOperators: ReadonlySet<string>): FormulaAstInfo {
  const symbols: string[] = [];
  const operators: FormulaOperatorUsage[] = [];
  const functions: string[] = [];
  let hasConditional = false;

  node.traverse((child: MathNode) => {
    switch (child.type) {
      case 'SymbolNode':
        if ('name' in child) {
          symbols.push(child.name as string);
        }
        break;
      case 'ConditionalNode':
        hasConditional = true;
        break;
      case 'OperatorNode':
        if ('op' in child) {
          operators.push({ op: child.op as string, fn: (child as MathNode & { fn?: string }).fn ?? '' });
        }
        break;
      case 'FunctionNode':
        if ('fn' in child) {
          const fn = child.fn as MathNode;
          if ('name' in fn) {
            functions.push(fn.name as string);
          }
        }
        break;
    }
  });

  return {
    symbols,
    operators,
    functions,
    hasConditional,
    outsideConditionalOperators: [...collectOutsideConditionalOperators(node, inConditionalOnlyOperators)]
  };
}

/**
 * Recursively collects every configured conditional-only **OperatorNode**
 * that exists outside of a ConditionalNode context.
 *
 * @param node                       - Current AST node to inspect
 * @param inConditionalOnlyOperators - Operators restricted to a conditional context
 * @param insideConditional          - Whether the current node is a descendant of a ConditionalNode
 * @param found                      - Accumulator of offending operator tokens
 */
function collectOutsideConditionalOperators(
  node: MathNode,
  inConditionalOnlyOperators: ReadonlySet<string>,
  insideConditional = false,
  found: Set<string> = new Set<string>()
): Set<string> {
  if (inConditionalOnlyOperators.size === 0) {
    return found;
  }

  if (node.type === 'ConditionalNode') {
    const cn = node as MathNode & { condition: MathNode; trueExpr: MathNode; falseExpr: MathNode };
    // All branches of a conditional are considered "inside conditional"
    collectOutsideConditionalOperators(cn.condition, inConditionalOnlyOperators, true, found);
    collectOutsideConditionalOperators(cn.trueExpr, inConditionalOnlyOperators, true, found);
    collectOutsideConditionalOperators(cn.falseExpr, inConditionalOnlyOperators, true, found);
    return found;
  }

  if (node.type === 'OperatorNode') {
    const on = node as MathNode & { op?: string; fn?: string; args?: MathNode[] };
    if (!insideConditional && isInConditionalOnlyOperator({ op: on.op ?? '', fn: on.fn ?? '' }, inConditionalOnlyOperators)) {
      found.add(on.op as string);
    }
    for (const arg of on.args ?? []) {
      collectOutsideConditionalOperators(arg, inConditionalOnlyOperators, insideConditional, found);
    }
    return found;
  }

  // Generic traversal for ArrayNode, ParenthesisNode, FunctionNode, etc.
  (node as MathNode & { forEach?: (cb: (child: MathNode) => void) => void }).forEach?.((child) => {
    collectOutsideConditionalOperators(child, inConditionalOnlyOperators, insideConditional, found);
  });
  return found;
}


/** Translation keys for all formula validation errors (maps to I18N_FORMULA.ERRORS.*) */
export const FORMULA_ERROR_KEYS = {
  IMPLICIT_MULTIPLICATION: 'I18N_FORMULA.ERRORS.IMPLICIT_MULTIPLICATION',
  UNKNOWN_VARIABLES: 'I18N_FORMULA.ERRORS.UNKNOWN_VARIABLES',
  DISALLOWED_CONSTANT: 'I18N_FORMULA.ERRORS.DISALLOWED_CONSTANT',
  DISALLOWED_TERNARY_OPERATOR: 'I18N_FORMULA.ERRORS.DISALLOWED_TERNARY_OPERATOR',
  AND_OR_OUTSIDE_CONDITIONAL: 'I18N_FORMULA.ERRORS.AND_OR_OUTSIDE_CONDITIONAL',
  DISALLOWED_OPERATORS: 'I18N_FORMULA.ERRORS.DISALLOWED_OPERATORS',
  DISALLOWED_FUNCTIONS: 'I18N_FORMULA.ERRORS.DISALLOWED_FUNCTIONS',
  INVALID_SYNTAX: 'I18N_FORMULA.ERRORS.INVALID_SYNTAX',
  FORMULA_TOO_LONG: 'I18N_FORMULA.ERRORS.FORMULA_TOO_LONG',
  /** evaluateFormula: one or more variable aliases used in the expression have null value */
  NULL_VARIABLE_VALUE: 'I18N_FORMULA.ERRORS.NULL_VARIABLE_VALUE',
  /** evaluateFormula: mathjs threw a runtime / syntax error during evaluation */
  EVALUATION_ERROR: 'I18N_FORMULA.ERRORS.EVALUATION_ERROR'
} as const;

/** Creates a failed validation result with sensible defaults. */
function errorResult(
  error: string,
  errorKey?: string,
  overrides?: Partial<FormulaValidationResult>,
  errorParams?: Record<string, unknown>
): FormulaValidationResult {
  return {
    valid: false,
    error,
    errorKey,
    errorParams,
    unknownVariables: [],
    disallowedOperators: [],
    disallowedFunctions: [],
    disallowedConstants: [],
    ...overrides
  };
}

/** Set of all known mathjs constant names (used to distinguish constants from unknown variables) */
const ALL_CONSTANTS_SET = new Set(ALL_CONSTANTS);

/** Set of all known mathjs function names (used to exclude them from symbol/unknown-variable validation) */
const ALL_FUNCTIONS_SET = new Set(ALL_FUNCTIONS);

/**
 * Validates a formula string using mathjs.
 * Checks:
 *  1. The formula is parseable (balanced parentheses, valid syntax)
 *  2. All variable tokens in the parsed AST exist in the variable list
 *  3. All operators are in the allowed list (when restrictOperators is true)
 *  4. All functions are in the allowed list (when restrictFunctions is true)
 *  5. All constants are in the allowed list (when restrictConstants is true)
 *
 * NOTE: Since mathjs does not support hyphens in variable names,
 * we temporarily replace hyphens in known variable names with underscores for parsing,
 * then map them back.
 */
export function validateFormula(
  formula: string,
  variableNames: string[],
  config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG,
  maxLength?: number
): FormulaValidationResult {
  const emptyResult: FormulaValidationResult = {
    valid: true,
    error: null,
    unknownVariables: [],
    disallowedOperators: [],
    disallowedFunctions: [],
    disallowedConstants: []
  };

  const trimmed = formula.trim();
  if (!trimmed) {
    return emptyResult;
  }

  // Mid-typing guard: if the formula ends with an operator token that expects a
  // right-hand operand (e.g. "var1 + var1^"), mathjs will throw a premature
  // "Unexpected end of expression" error. Return valid so the editor stays quiet
  // while the user is still typing.
  if (/\^$/.test(trimmed)) {
    return emptyResult;
  }

  if (maxLength !== undefined && maxLength > 0 && trimmed.length > maxLength) {
    return errorResult(
      `Formula exceeds maximum length of ${maxLength} characters.`,
      FORMULA_ERROR_KEYS.FORMULA_TOO_LONG,
      {},
      { maxLength, current: trimmed.length }
    );
  }

  // Build mapping: hyphenated -> underscore version
  const hyphenToUnderscore = new Map<string, string>();
  const underscoreToHyphen = new Map<string, string>();
  const validNamesSet = new Set<string>();

  for (const name of variableNames) {
    const safeName = name.replace(/-/g, '_');
    hyphenToUnderscore.set(name, safeName);
    underscoreToHyphen.set(safeName, name);
    validNamesSet.add(safeName);
  }

  // Replace hyphenated variable names with underscore versions in the formula
  let safeFormula = trimmed;
  // Sort by length descending to avoid partial replacements
  const sortedNames = [...variableNames].sort((a, b) => b.length - a.length);
  for (const name of sortedNames) {
    if (name.includes('-')) {
      const safeName = hyphenToUnderscore.get(name);
      if (safeName) {
        safeFormula = safeFormula.split(name).join(safeName);
      }
    }
  }

  // Precompute effective sets
  const allowedFunctions = getEffectiveFunctions(config);
  const allowedConstants = getEffectiveConstants(config);
  const allowedOperators = getEffectiveOperators(config);
  const inConditionalOnlyOperators = getInConditionalOnlyOperators(config);

  try {
    // Parse formula directly — users type 'and'/'or' keywords; '&&'/'||' are never present.
    // const tree = parse(normalizeFormulaForMathJs(safeFormula));
    const tree = parse(safeFormula);

    // Reject implicit multiplication (e.g. "var1 var2" parsed as "var1 * var2" with implicit: true)
    if (hasImplicitMultiplication(tree)) {
      return errorResult(
        'Variables must be separated by operators. Implicit multiplication (e.g. "var1 var2") is not allowed.',
        FORMULA_ERROR_KEYS.IMPLICIT_MULTIPLICATION
      );
    }

    const { symbols, operators, functions, hasConditional, outsideConditionalOperators } = extractAstInfo(tree, inConditionalOnlyOperators);

    // Separate symbols into constants vs unknown variables
    const unknownVariables: string[] = [];
    const disallowedConstants: string[] = [];

    for (const symbol of symbols) {
      // Skip known variable names and known functions (functions are validated separately)
      if (validNamesSet.has(symbol) || ALL_FUNCTIONS_SET.has(symbol)) {
        continue;
      }

      // Check if it's a known constant
      if (ALL_CONSTANTS_SET.has(symbol)) {
        if (config.restrictConstants && !allowedConstants.has(symbol)) {
          if (!disallowedConstants.includes(symbol)) {
            disallowedConstants.push(symbol);
          }
        }
        continue;
      }

      // Unknown variable
      const originalName = underscoreToHyphen.get(symbol) ?? symbol;
      if (!unknownVariables.includes(originalName)) {
        unknownVariables.push(originalName);
      }
    }

    if (unknownVariables.length > 0) {
      return errorResult(
        `Unknown variable(s): ${unknownVariables.join(', ')}`,
        FORMULA_ERROR_KEYS.UNKNOWN_VARIABLES,
        { unknownVariables },
        { variables: unknownVariables.join(', ') }
      );
    }

    if (disallowedConstants.length > 0) {
      return errorResult(
        `Constant '${disallowedConstants.join("', '")}' is not allowed`,
        FORMULA_ERROR_KEYS.DISALLOWED_CONSTANT,
        { disallowedConstants },
        { constants: disallowedConstants.join("', '") }
      );
    }

    // Check disallowed ternary (ConditionalNode)
    // If '?:' is NOT in allowedOperators and a conditional is found → error
    if (config.restrictOperators && !config.allowedOperators.includes('?:') && hasConditional) {
      return errorResult("Operator '?:' (ternary conditional) is not allowed", FORMULA_ERROR_KEYS.DISALLOWED_TERNARY_OPERATOR, { disallowedOperators: ['?:'] });
    }

    // Check configured conditional-only operators used outside a conditional context
    if (outsideConditionalOperators.length > 0) {
      return errorResult(
        `Operator '${outsideConditionalOperators.join("', '")}' is only allowed inside a conditional expression ('?:').\n` +
          'Example: (var1 > 0 and var2 < 10) ? var3 : var4',
        FORMULA_ERROR_KEYS.AND_OR_OUTSIDE_CONDITIONAL,
        { disallowedOperators: outsideConditionalOperators },
        { operators: outsideConditionalOperators.join("', '") }
      );
    }

    // Check disallowed operators
    const disallowedOperators: string[] = [];
    if (config.restrictOperators) {
      for (const usage of operators) {
        const { op } = usage;
        // Conditional-only operators are governed by `inConditionalOnlyOperators` (checked above)
        if (isInConditionalOnlyOperator(usage, inConditionalOnlyOperators)) {
          continue;
        }
        if (!allowedOperators.has(op) && !disallowedOperators.includes(op)) {
          disallowedOperators.push(op);
        }
      }
    }

    if (disallowedOperators.length > 0) {
      return errorResult(
        `Operator '${disallowedOperators.join("', '")}' is not allowed`,
        FORMULA_ERROR_KEYS.DISALLOWED_OPERATORS,
        { disallowedOperators },
        { operators: disallowedOperators.join("', '") }
      );
    }

    // Check disallowed functions
    const disallowedFunctions: string[] = [];
    if (config.restrictFunctions) {
      for (const fn of functions) {
        if (!allowedFunctions.has(fn) && !disallowedFunctions.includes(fn)) {
          disallowedFunctions.push(fn);
        }
      }
    }

    if (disallowedFunctions.length > 0) {
      return errorResult(
        `Function '${disallowedFunctions.join("', '")}' is not allowed`,
        FORMULA_ERROR_KEYS.DISALLOWED_FUNCTIONS,
        { disallowedFunctions },
        { functions: disallowedFunctions.join("', '") }
      );
    }

    return emptyResult;
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Invalid formula syntax';
    return errorResult(message, FORMULA_ERROR_KEYS.INVALID_SYNTAX);
  }
}

/**
 * CodeMirror lint source that validates the formula using mathjs.
 */
export function formulaLintSource(variableNames: string[], config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG, maxLength?: number) {
  return (view: EditorView): Diagnostic[] => {
    const doc = view.state.doc.toString();
    const result = validateFormula(doc, variableNames, config, maxLength);

    if (result.valid) {
      return [];
    }

    return [
      {
        from: 0,
        to: doc.length,
        severity: 'error',
        message: result.error ?? 'Invalid formula'
      }
    ];
  };
}

/**
 * Returns true if the AST contains any implicit-multiplication node (concatenated terms).
 * mathjs silently parses `a b` as `a * b` with `implicit: true` on the OperatorNode.
 * Uses a recursive early-exit pattern consistent with `collectOutsideConditionalOperators`.
 */
function hasImplicitMultiplication(node: MathNode): boolean {
  if (node.type === 'OperatorNode') {
    const on = node as MathNode & { implicit?: boolean; args?: MathNode[] };
    if (on.implicit === true) return true;
    for (const arg of on.args ?? []) {
      if (hasImplicitMultiplication(arg)) return true;
    }
    return false;
  }
  let found = false;
  (node as MathNode & { forEach?: (cb: (c: MathNode) => void) => void }).forEach?.((child) => {
    if (!found) found = hasImplicitMultiplication(child);
  });
  return found;
}
